import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const loadLogoBase64 = async () => {
  const response = await fetch('/images/logosheet.jpg');
  if (!response.ok) {
    return null;
  }

  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result));
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

const formatExportDate = (dateValue) => {
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) {
    return 'DD MM YYYY';
  }

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = String(date.getFullYear());
  return `${day} ${month} ${year}`;
};

const buildExportFilename = (report) => {
  const shiftNumber = Number(report?.shift);
  const shiftLabel = [1, 2, 3].includes(shiftNumber) ? String(shiftNumber) : 'Int';
  return `RGA Daily Shift ${shiftLabel} Report – ${formatExportDate(report?.date)}.xlsx`;
};

const exportStyledExcel = async (report) => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Shift Report');

  worksheet.pageSetup = {
    fitToPage: true,
    fitToWidth: 1,
    fitToHeight: 0
  };

  try {
    const logoBase64 = await loadLogoBase64();
    if (logoBase64) {
      const logo = workbook.addImage({
        base64: logoBase64,
        extension: 'jpeg'
      });
      worksheet.addImage(logo, 'A1:C2');
    }
  } catch (_) {
    // Continue export if logo fails to load.
  }

  const centeredStyle = { vertical: 'middle', horizontal: 'center' };
  const boldFont = { bold: true };
  const leftStyle = { vertical: 'middle', horizontal: 'left' };

  worksheet.getRow(1).height = 30;

  worksheet.mergeCells('D1:G1');
  worksheet.getCell('D1').value = `Daily Report for Shift No. ${report.shift}`;
  worksheet.getCell('D1').font = { ...boldFont, size: 16 };
  worksheet.getCell('D1').alignment = centeredStyle;

  worksheet.mergeCells('E2:F2');
  worksheet.getCell('E2').value = report.shiftTime || '';
  worksheet.getCell('E2').font = { size: 14 };
  worksheet.getCell('E2').alignment = centeredStyle;

  worksheet.mergeCells('H1:I1');
  worksheet.getCell('H1').value = 'Prepared by RGA';
  worksheet.getCell('H1').alignment = centeredStyle;

  worksheet.mergeCells('H2:I4');
  worksheet.getCell('H2').value =
    'Date:            ' +
    (Number(report.shift) === 3
      ? `${new Date(report.date).toLocaleDateString()}\n to \n${new Date(
        report.secondDate || new Date(new Date(report.date).setDate(new Date(report.date).getDate() + 1))
      ).toLocaleDateString()}`
      : new Date(report.date).toLocaleDateString());
  worksheet.getCell('H2').alignment = { vertical: 'middle', horizontal: 'right', wrapText: true };

  worksheet.mergeCells('A3:C4');
  worksheet.getCell('A3').value = `RGA In Charge: ${report.inCharge || ''}`;
  worksheet.getCell('A3').font = { size: 12 };
  worksheet.getCell('A3').alignment = leftStyle;

  worksheet.getColumn('A').width = 10;
  worksheet.getColumn('B').width = 6;
  worksheet.getColumn('C').width = 15;
  worksheet.getColumn('D').width = 12;
  worksheet.getColumn('E').width = 10;
  worksheet.getColumn('F').width = 10;
  worksheet.getColumn('G').width = 15;
  worksheet.getColumn('H').width = 7;
  worksheet.getColumn('I').width = 15;

  const applyBorder = (cell) => {
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' }
    };
  };

  const calcHeight = (value, startCol, endCol) => {
    const text = String(value || '');
    if (!text.trim()) {
      return 18;
    }

    let totalWidth = 0;
    for (let col = startCol; col <= endCol; col += 1) {
      totalWidth += worksheet.getColumn(col).width || 10;
    }

    const charsPerLine = Math.max(8, Math.floor(totalWidth));
    const lines = text.split('\n').reduce((sum, line) => {
      const lineLength = line.length || 1;
      return sum + Math.ceil(lineLength / charsPerLine);
    }, 0);

    return Math.max(18, lines * 15);
  };

  const styleSectionTitle = (row, color) => {
    row.eachCell((cell) => {
      cell.font = { bold: true, color: { argb: 'FF000000' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: color } };
      cell.alignment = { vertical: 'middle', horizontal: 'center' };
      applyBorder(cell);
    });
  };

  const styleHeader = (row, color) => {
    row.eachCell((cell) => {
      cell.font = { bold: true, color: { argb: 'FF000000' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: color } };
      cell.alignment = { wrapText: true, vertical: 'middle', horizontal: 'center' };
      applyBorder(cell);
    });
  };

  const styleDataRow = (row, notesColumn) => {
    row.eachCell((cell, colNumber) => {
      applyBorder(cell);
      cell.alignment = { wrapText: true, vertical: 'middle', horizontal: 'center' };
      if (colNumber === notesColumn) {
        cell.alignment = { wrapText: true, vertical: 'top', horizontal: 'left' };
      }
    });
  };

  worksheet.addRow([]);
  let titleRow = worksheet.addRow(['SCR']);
  styleSectionTitle(titleRow, 'FF4FADEA');
  worksheet.mergeCells(`A${titleRow.number}:I${titleRow.number}`);

  let headerRow = worksheet.addRow(['Time', 'No', 'Tag', 'Status', 'Notes/Observation/Actions']);
  styleHeader(headerRow, 'FF4FADEA');
  worksheet.mergeCells(`E${headerRow.number}:I${headerRow.number}`);

  (Array.isArray(report.scr) ? report.scr : []).forEach((scr) => {
    const dataRow = worksheet.addRow([scr.time || '', scr.number ?? '', scr.tag || '', scr.status || '', scr.notes || '']);
    worksheet.mergeCells(`E${dataRow.number}:I${dataRow.number}`);
    styleDataRow(dataRow, 5);
    dataRow.height = calcHeight(dataRow.getCell(5).value, 5, 9);
  });

  worksheet.addRow([]);
  titleRow = worksheet.addRow(['Microphone Station Group']);
  styleSectionTitle(titleRow, 'FFFFFF54');
  worksheet.mergeCells(`A${titleRow.number}:I${titleRow.number}`);

  headerRow = worksheet.addRow(['Time', 'Area', '', 'Status', 'Notes/Observation/Actions']);
  styleHeader(headerRow, 'FFFFFF54');
  worksheet.mergeCells(`B${headerRow.number}:C${headerRow.number}`);
  worksheet.mergeCells(`E${headerRow.number}:I${headerRow.number}`);

  (Array.isArray(report.microphoneStationGroup) ? report.microphoneStationGroup : []).forEach((msg) => {
    const dataRow = worksheet.addRow([msg.time || '', msg.area || '', '', msg.status || '', msg.notes || '']);
    worksheet.mergeCells(`B${dataRow.number}:C${dataRow.number}`);
    worksheet.mergeCells(`E${dataRow.number}:I${dataRow.number}`);
    styleDataRow(dataRow, 5);
    dataRow.height = calcHeight(dataRow.getCell(5).value, 5, 9);
  });

  worksheet.addRow([]);
  titleRow = worksheet.addRow(['Corrective Maintenance']);
  styleSectionTitle(titleRow, 'FF9FCE63');
  worksheet.mergeCells(`A${titleRow.number}:I${titleRow.number}`);

  headerRow = worksheet.addRow(['Time', 'CM No', '', 'Status', 'Notes/Observation/Actions']);
  styleHeader(headerRow, 'FF9FCE63');
  worksheet.mergeCells(`B${headerRow.number}:C${headerRow.number}`);
  worksheet.mergeCells(`E${headerRow.number}:I${headerRow.number}`);

  (Array.isArray(report.correctiveMaintenance) ? report.correctiveMaintenance : []).forEach((cm) => {
    const dataRow = worksheet.addRow([cm.time || '', cm.cmNo || '', '', cm.status || '', cm.notes || '']);
    worksheet.mergeCells(`B${dataRow.number}:C${dataRow.number}`);
    worksheet.mergeCells(`E${dataRow.number}:I${dataRow.number}`);
    styleDataRow(dataRow, 5);
    dataRow.height = calcHeight(dataRow.getCell(5).value, 5, 9);
  });

  worksheet.addRow([]);
  titleRow = worksheet.addRow(['System Log']);
  styleSectionTitle(titleRow, 'FFCBDFB8');
  worksheet.mergeCells(`A${titleRow.number}:I${titleRow.number}`);

  headerRow = worksheet.addRow(['Time', 'Log Type', '', 'Status', 'Notes/Observation/Actions']);
  styleHeader(headerRow, 'FFCBDFB8');
  worksheet.mergeCells(`B${headerRow.number}:C${headerRow.number}`);
  worksheet.mergeCells(`E${headerRow.number}:I${headerRow.number}`);

  (Array.isArray(report.systemFaultLog) ? report.systemFaultLog : []).forEach((sfl) => {
    const dataRow = worksheet.addRow([sfl.time || '', sfl.logType || '', '', sfl.status || '', sfl.notes || '']);
    worksheet.mergeCells(`B${dataRow.number}:C${dataRow.number}`);
    worksheet.mergeCells(`E${dataRow.number}:I${dataRow.number}`);
    styleDataRow(dataRow, 5);
    dataRow.height = calcHeight(dataRow.getCell(5).value, 5, 9);
  });

  worksheet.addRow([]);
  titleRow = worksheet.addRow(['PPM']);
  styleSectionTitle(titleRow, 'FFF9DA78');
  worksheet.mergeCells(`A${titleRow.number}:I${titleRow.number}`);

  headerRow = worksheet.addRow(['Time', 'PPM No', '', 'Status', 'Notes/Observation/Actions']);
  styleHeader(headerRow, 'FFF9DA78');
  worksheet.mergeCells(`B${headerRow.number}:C${headerRow.number}`);
  worksheet.mergeCells(`E${headerRow.number}:I${headerRow.number}`);

  (Array.isArray(report.ppm) ? report.ppm : []).forEach((ppm) => {
    const dataRow = worksheet.addRow([ppm.time || '', ppm.ppmNo || '', '', ppm.status || '', ppm.notes || '']);
    worksheet.mergeCells(`B${dataRow.number}:C${dataRow.number}`);
    worksheet.mergeCells(`E${dataRow.number}:I${dataRow.number}`);
    styleDataRow(dataRow, 5);
    dataRow.height = calcHeight(dataRow.getCell(5).value, 5, 9);
  });

  worksheet.addRow([]);
  titleRow = worksheet.addRow(['Others']);
  styleSectionTitle(titleRow, 'FF4FADEB');
  worksheet.mergeCells(`A${titleRow.number}:I${titleRow.number}`);

  headerRow = worksheet.addRow(['Time', 'Area', '', 'Notes/Observation/Actions']);
  styleHeader(headerRow, 'FF4FADEB');
  worksheet.mergeCells(`B${headerRow.number}:C${headerRow.number}`);
  worksheet.mergeCells(`D${headerRow.number}:I${headerRow.number}`);

  (Array.isArray(report.others) ? report.others : []).forEach((other) => {
    const dataRow = worksheet.addRow([other.time || '', other.area || '', '', other.notes || '']);
    worksheet.mergeCells(`B${dataRow.number}:C${dataRow.number}`);
    worksheet.mergeCells(`D${dataRow.number}:I${dataRow.number}`);
    styleDataRow(dataRow, 4);
    dataRow.height = calcHeight(dataRow.getCell(4).value, 4, 9);
  });

  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(new Blob([buffer]), buildExportFilename(report));
};

export default exportStyledExcel;
